import React, { useState } from 'react';
import { Share2, Copy, Check, MessageCircle, Mail, Link, QrCode, X, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { QRCodeSVG } from 'qrcode.react';

export default function ShareApp({ isOpen, onClose, inviteCode, organizationName }) {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const appUrl = window.location.origin;
  const shareMessage = `Join ${organizationName || 'our security team'} on TheSecurityGuardApp! 
  
Use invite code: ${inviteCode}
Download the app: ${appUrl}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join TheSecurityGuardApp',
          text: shareMessage,
          url: appUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };

  const handleWhatsAppShare = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`Join ${organizationName || 'our team'} on TheSecurityGuardApp`);
    const body = encodeURIComponent(shareMessage);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleSMSShare = () => {
    const smsUrl = `sms:?body=${encodeURIComponent(shareMessage)}`;
    window.location.href = smsUrl;
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-700" />
            Share App & Invite
          </DialogTitle>
          <DialogDescription>
            Invite guards to join your organization
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Invite Code Display */}
          <div className="bg-blue-50 p-4 rounded-lg text-center">
            <p className="text-sm text-blue-700 mb-1">Organization Invite Code</p>
            <p className="text-2xl font-bold font-mono text-blue-800 tracking-wider">
              {inviteCode || 'GP-XXXXXXXX'}
            </p>
          </div>

          {/* QR Code Toggle */}
          {showQR ? (
            <div className="text-center">
              <div className="bg-white p-4 rounded-lg border inline-block">
                <QRCodeSVG 
                  value={`${appUrl}/register?code=${inviteCode}`}
                  size={180}
                  level="M"
                  includeMargin={true}
                />
              </div>
              <p className="text-sm text-slate-500 mt-2">Scan to join</p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowQR(false)}
                className="mt-2"
              >
                Hide QR Code
              </Button>
            </div>
          ) : (
            <Button 
              variant="outline" 
              onClick={() => setShowQR(true)}
              className="w-full gap-2"
            >
              <QrCode className="w-4 h-4" />
              Show QR Code
            </Button>
          )}

          {/* Share Options */}
          <div className="grid grid-cols-2 gap-3">
            {/* Native Share (if supported) */}
            {navigator.share && (
              <Button 
                onClick={handleNativeShare}
                className="bg-blue-700 hover:bg-blue-800 gap-2"
              >
                <Smartphone className="w-4 h-4" />
                Share
              </Button>
            )}

            {/* Copy Link */}
            <Button 
              onClick={handleCopyLink}
              variant="outline"
              className="gap-2"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy
                </>
              )}
            </Button>

            {/* WhatsApp */}
            <Button 
              onClick={handleWhatsAppShare}
              className="bg-green-600 hover:bg-green-700 gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </Button>

            {/* Email */}
            <Button 
              onClick={handleEmailShare}
              variant="outline"
              className="gap-2"
            >
              <Mail className="w-4 h-4" />
              Email
            </Button>

            {/* SMS */}
            <Button 
              onClick={handleSMSShare}
              variant="outline"
              className="gap-2 col-span-2"
            >
              <MessageCircle className="w-4 h-4" />
              Send SMS
            </Button>
          </div>

          {/* Instructions */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <p className="text-sm font-medium text-slate-700 mb-2">How to invite guards:</p>
            <ol className="text-sm text-slate-600 list-decimal list-inside space-y-1">
              <li>Share the invite code or QR code</li>
              <li>Guards register using the code</li>
              <li>Approve their join request in Admin Panel</li>
            </ol>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
